import React, { useEffect, useState } from "react";
import MainLayout from "../../layouts/MainLayout";
import { createLookupApi } from "../../api/lookupApi";
import { listClassConfigurations } from "../../api/classConfigurationApi";
import { listStudents, pushbackMigration } from "../../api/studentApi";
import "../Attendance/Attendance.css";

const academicYearApi = createLookupApi("academic-years");

/**
 * Direct port of digicampusbd's `student-migration-pushback` ("push back")
 * — undo a migration: hard-deletes the current (newer) `StudentIdentification`
 * row and reactivates the previous year's row (flips its `migrationStatus`
 * back to true). Confirmed via `StudentMigrationService.pushbackMigration`
 * that the only real guard is "the pushback year must differ from the
 * current year" (a plain string `!equals`, not a numeric "earlier than"
 * comparison despite the old app's own error message implying ordering) —
 * ported exactly, including that same not-actually-ordering check. New
 * `POST /api/students/migration/push-back`. Requires a prior real
 * `student-migration` (Roll Wise) run for the same student to have a
 * previous-year row to push back onto — verified live against exactly such
 * a fixture pair.
 */
export default function StudentMigrationPushbackPage() {
  const [academicYears, setAcademicYears] = useState([]);
  const [currentYear, setCurrentYear] = useState("");
  const [classConfigs, setClassConfigs] = useState([]);
  const [classConfigId, setClassConfigId] = useState("");
  const [pushbackYear, setPushbackYear] = useState("");
  const [rows, setRows] = useState(null);
  const [selectedIds, setSelectedIds] = useState([]);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    academicYearApi.list().then((years) => {
      setAcademicYears(years);
      if (years.length > 0) {
        setCurrentYear(years[0].name);
        if (years.length > 1) setPushbackYear(years[1].name);
      }
    });
    listClassConfigurations().then((configs) => {
      setClassConfigs(configs);
      if (configs.length > 0) setClassConfigId(String(configs[0].id));
    });
  }, []);

  const handleSearch = async () => {
    setError("");
    setSuccess("");
    if (!currentYear || !classConfigId) {
      setError("Please select the current academic year and section.");
      return;
    }
    try {
      const students = await listStudents(currentYear, classConfigId);
      setRows(students.sort((a, b) => (a.studentRoll || 0) - (b.studentRoll || 0)));
      setSelectedIds([]);
    } catch (err) {
      setError("Failed to load students.");
    }
  };

  const toggleSelect = (id) => setSelectedIds((prev) => (prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]));

  const handlePushback = async () => {
    setError("");
    setSuccess("");
    if (!pushbackYear) {
      setError("Please select the academic year to push back to.");
      return;
    }
    if (selectedIds.length === 0) {
      setError("Please select at least one student.");
      return;
    }
    setSubmitting(true);
    try {
      await pushbackMigration({ acYearToPushback: pushbackYear, currentIdentificationIds: selectedIds });
      setSuccess(`Pushed back ${selectedIds.length} students to ${pushbackYear}.`);
      handleSearch();
    } catch (err) {
      setError((err.response && err.response.data && err.response.data.message) || "Push back failed.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <MainLayout>
      <div className="page-header">
        <h1>Student Migration — Push Back</h1>
      </div>
      {error && <p style={{ color: "var(--color-danger)" }}>{error}</p>}
      {success && <p style={{ color: "var(--color-success, green)" }}>{success}</p>}
      <div className="attendance-filters">
        <label>
          Current Academic Year
          <select value={currentYear} onChange={(e) => setCurrentYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <label>
          Section
          <select value={classConfigId} onChange={(e) => setClassConfigId(e.target.value)}>
            {classConfigs.map((c) => (
              <option key={c.id} value={c.id}>
                {c.classConfigName}
              </option>
            ))}
          </select>
        </label>
        <label>
          Push Back To
          <select value={pushbackYear} onChange={(e) => setPushbackYear(e.target.value)}>
            {academicYears.map((y) => (
              <option key={y.id} value={y.name}>
                {y.name}
              </option>
            ))}
          </select>
        </label>
        <button type="button" className="btn-primary" onClick={handleSearch}>
          Load Students
        </button>
      </div>
      {rows && (
        <>
          <div className="table-scroll">
            <table className="attendance-table">
              <thead>
                <tr>
                  <th></th>
                  <th>Student ID</th>
                  <th>Student Name</th>
                  <th>Roll</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r.identificationId}>
                    <td>
                      <input
                        type="checkbox"
                        checked={selectedIds.includes(r.identificationId)}
                        onChange={() => toggleSelect(r.identificationId)}
                      />
                    </td>
                    <td>{r.customStudentId}</td>
                    <td>{r.studentName}</td>
                    <td>{r.studentRoll}</td>
                  </tr>
                ))}
                {rows.length === 0 && (
                  <tr>
                    <td colSpan={4}>No students found for this selection.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          {rows.length > 0 && (
            <button type="button" className="btn-primary" onClick={handlePushback} disabled={submitting}>
              {submitting ? "Pushing back..." : `Push Back ${selectedIds.length} Selected`}
            </button>
          )}
        </>
      )}
    </MainLayout>
  );
}
